import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw } from "vue-router";
// store para recoger el token y para setear los menús
import { useAuthStore } from "@/stores/auth";
import { useAppSidebarMenuStore } from "@/stores/app-sidebar-menu";
import { useAppOptionStore } from "@/stores/app-option";
// endpoints
import { getEndpoints } from "@/services/apiServices/endpoints";
// axios
import axios from "axios";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: () => {
      // Normal PWA:
      const remember = JSON.parse(
        localStorage.getItem("rememberSetup") || "false",
      );
      const last = localStorage.getItem("lastPath");

      const validLastPaths = [
        "/customer/home",
        "/estadisticas",
        "/pedidos",
        "/ordenes-trabajo",
        "/ordenes-trabajo/nueva",
      ];

      // si eligió “recordar” y hay lastPath válida, vamos ahí
      if (remember && last && validLastPaths.includes(last)) {
        return last;
      }

      // en otro caso, al login
      return "/login";
    },
  },
  {
    path: "/login",
    name: "login",
    component: () =>
      import(/* webpackChunkName: "login" */ "../views/LoginView.vue"),
    meta: {
      requireAuth: false,
    },
  },
  {
    path: "/restablecer-password/:token",
    name: "restablecer-password",
    component: () =>
      import(
        /* webpackChunkName: "password-restore" */ "../views/PasswordRestoreView.vue"
      ),
    meta: {
      requireAuth: false,
    },
  },
  {
    path: "/customer/home",
    name: "home",
    component: () =>
      import(/* webpackChunkName: "home" */ "../views/customer/Home.vue"),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/estadisticas",
    name: "Estadísticas",
    component: () =>
      import(
        /* webpackChunkName: "Management" */ "../views/management/ManagementEntry.vue"
      ),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/pedidos",
    name: "shopping",
    component: () => import("@/views/shopping/ShoppingView.vue"),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/ordenes-trabajo",
    name: "work-orders",
    component: () => import("@/views/work-orders/WorkOrdersLanding.vue"),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/ordenes-trabajo/nueva",
    name: "work-orders-create",
    component: () => import("@/views/work-orders/WorkOrderCreateView.vue"),
    meta: {
      requireAuth: true,
    },
  },
  // Redirige cualquier otra ruta desconocida de la PWA normal
  {
    path: "/:pathMatch(.*)*",
    redirect: () => {
      const token = localStorage.getItem("token");
      return token ? "/customer/home" : "/login";
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});
// validación del token
const validateToken = async (token: string): Promise<boolean> => {
  const lang = localStorage.getItem("language");
  let config = {
    headers: {
      "Accept-Language": lang,
    },
  };
  // endpoint
  const endpoints = getEndpoints();
  const URL = endpoints.checkToken;
  // envio del token actual
  let userToken = {
    token: token,
  };
  const response = await axios.post(URL, userToken, config);
  if (response.data)
    return true; // si la respuesta es true el token es válido
  else return false;
};
// PROTECCIÓN DE RUTAS
// con el método beforeEach especificamos que queremos hacer antes de ir a cada ruta
// para ello establecemos una GUARDA.
// se reciben tres argumentos:
//  - to: a donde se pretende navegar el usuario
//  - from: de donde se viene
//  - next: hacia donde va a ir el usuario
router.beforeEach(async (to, from, next) => {
  // instancia de la store
  const authStore = useAuthStore();

  const appOption = useAppOptionStore();
  // token
  const token = localStorage.getItem("token") || authStore.getJwt || "";
  // se comprueba si existe token
  const auth = token !== "";
  // se guarda si a donde quiere ir el usuario requiere autenticación
  const needAuth = to.meta.requireAuth;
  // se comprueba si es una recarga de página o si el usuario cearro y volvió a abrir la pestaña o ventana
  const reloadDetected = localStorage.getItem("reloadDetected");
  // si se requiere autenticación y el usuario no esta autenticado (no existe token) se le dirigue a login para que se autentique

  if (needAuth && !auth) {
    return next("/login");
  }

  if (needAuth && auth && !reloadDetected) {
    return next();
  }

  if (needAuth && auth && reloadDetected) {
    try {
      const isValidToken = await validateToken(token);

      if (!isValidToken) {
        await authStore.logOut();
        return next("/login");
      }

      appOption.appSelectedMenu = localStorage.getItem("selectedMenu") || "";
      appOption.appSelectedMenuIcon =
        localStorage.getItem("selectedMenuIcon") || "";

      const appSidebarMenuStore = useAppSidebarMenuStore();
      appSidebarMenuStore.setMenuItems();

      localStorage.removeItem("reloadDetected");
      localStorage.removeItem("selectedMenu");
      localStorage.removeItem("selectedMenuIcon");

      return next();
    } catch (error) {
      console.error("Error al validar el token:", error);
      await authStore.logOut();
      return next("/login");
    }
  }

  return next();
});

export { routes };
export default router;
